import { useEffect, useMemo, useRef, useState } from 'react';
import { Pause, Play, RotateCcw, RotateCw } from 'lucide-react';
import { useWavesurfer } from '@wavesurfer/react';
import { useDetailPanelInteractionValue, type PanelSpan } from '@/components/shared';
import { useBackofficePreference } from '@/preferences';
import { useStableRecordingUrl } from './useStableRecordingUrl';
import { formatDuration } from '../format';
import { InterviewDetailPanel } from './InterviewDetailPanelGrid';
import {
  InterviewRecordingTimeShareButton,
  type RecordingTimeShareLocation,
} from './InterviewRecordingTimeShareButton';
import {
  INTERVIEW_RECORDING_INTERACTION_KEY,
  type InterviewRecordingInteractionState,
} from './interviewTileInteractions';

export interface InterviewRecordingPanelProps {
  recordingUrl: string | null;
  waveformSourceUrl?: string | null | undefined;
  waveformPeaks?: RecordingWaveformPeaks | null | undefined;
  durationSeconds: number | null;
  panelId?: string | undefined;
  currentSpan?: PanelSpan | undefined;
  className?: string | undefined;
  initialSeekTimeSeconds?: number | undefined;
  preferenceScope?: string | undefined;
  shareLocation?: RecordingTimeShareLocation | undefined;
}

export interface RecordingWaveformPeaks {
  durationSeconds: number;
  peaks: number[][];
  channelRoles?: Array<'participant' | 'interviewer'>;
}

export function formatAudioTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '0:00';
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

function isWideSpan(span: PanelSpan | undefined): boolean {
  return span !== undefined && Number(span.slice(0, 1)) > 1;
}

function clampAudioTime(seconds: number, durationSeconds: number): number {
  if (!Number.isFinite(seconds)) {
    return 0;
  }

  const lowerBounded = Math.max(0, seconds);
  if (!durationSeconds) {
    return lowerBounded;
  }
  return Math.min(lowerBounded, durationSeconds);
}

export function InterviewRecordingPanel({
  recordingUrl: refreshedRecordingUrl,
  waveformSourceUrl: refreshedWaveformSourceUrl,
  waveformPeaks,
  durationSeconds,
  panelId,
  currentSpan,
  className,
  initialSeekTimeSeconds,
  preferenceScope,
  shareLocation,
}: InterviewRecordingPanelProps) {
  const [waveformFailed, setWaveformFailed] = useState(false);
  const defaultSettings = useMemo(
    () => ({ showSkipControls: true, autoScrollTranscript: true }),
    []
  );
  const preference = useBackofficePreference(preferenceScope, {
    defaultValue: defaultSettings,
    debounceMs: 400,
  });
  const [showSkipControls, setShowSkipControls] = useState(true);
  const [autoScrollTranscript, setAutoScrollTranscript] = useState(true);
  const [recordingInteraction] = useDetailPanelInteractionValue<InterviewRecordingInteractionState>(
    INTERVIEW_RECORDING_INTERACTION_KEY
  );
  const recordingUrl = useStableRecordingUrl(
    refreshedRecordingUrl,
    recordingInteraction?.isPlaying ?? false
  );
  const waveformSourceUrl = useStableRecordingUrl(
    refreshedWaveformSourceUrl ?? null,
    recordingInteraction?.isPlaying ?? false
  );
  const hasWaveformSource = Boolean(waveformPeaks) || Boolean(waveformSourceUrl ?? recordingUrl);
  const useWaveformLayout =
    recordingUrl !== null && hasWaveformSource && isWideSpan(currentSpan) && !waveformFailed;
  const isCompactSquare = currentSpan === '1x1';
  const currentShareTimeSeconds = recordingInteraction?.currentTimeSeconds ?? 0;

  useEffect(() => {
    setWaveformFailed(false);
  }, [recordingUrl]);

  useEffect(() => {
    if (!preference.loaded) {
      return;
    }

    setShowSkipControls(preference.value.showSkipControls);
    setAutoScrollTranscript(preference.value.autoScrollTranscript);
  }, [preference.loaded, preference.value.autoScrollTranscript, preference.value.showSkipControls]);

  function handleShowSkipControlsChange(nextValue: boolean) {
    setShowSkipControls(nextValue);
    preference.setValue((current) => ({ ...current, showSkipControls: nextValue }));
  }

  function handleAutoScrollTranscriptChange(nextValue: boolean) {
    setAutoScrollTranscript(nextValue);
    preference.setValue((current) => ({ ...current, autoScrollTranscript: nextValue }));
  }

  return (
    <InterviewDetailPanel
      title="Recording"
      span="3x2"
      panelId={panelId}
      currentSpan={currentSpan}
      className={className}
      headerActions={
        <InterviewRecordingTimeShareButton
          timeSeconds={currentShareTimeSeconds}
          location={shareLocation}
        />
      }
      settingsContent={
        <>
          <label className="flex items-center justify-between gap-4 rounded-md border border-neutral-200 px-3 py-2 text-body font-semibold text-neutral-900">
            <span>Show rewind/skip buttons</span>
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-neutral-300 text-lavender-700"
              checked={showSkipControls}
              onChange={(event) => handleShowSkipControlsChange(event.target.checked)}
            />
          </label>
          <label className="flex items-center justify-between gap-4 rounded-md border border-neutral-200 px-3 py-2 text-body font-semibold text-neutral-900">
            <span>Auto-scroll transcript</span>
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-neutral-300 text-lavender-700"
              checked={autoScrollTranscript}
              onChange={(event) => handleAutoScrollTranscriptChange(event.target.checked)}
            />
          </label>
        </>
      }
    >
      {useWaveformLayout ? (
        <WaveformRecordingPlayer
          recordingUrl={recordingUrl}
          waveformSourceUrl={waveformSourceUrl}
          waveformPeaks={waveformPeaks}
          durationSeconds={durationSeconds}
          onWaveformError={() => setWaveformFailed(true)}
          showSkipControls={showSkipControls}
          autoScrollTranscript={autoScrollTranscript}
          initialSeekTimeSeconds={initialSeekTimeSeconds}
        />
      ) : (
        <NativeRecordingPlayer
          recordingUrl={recordingUrl}
          durationSeconds={durationSeconds}
          showSkipControls={showSkipControls}
          autoScrollTranscript={autoScrollTranscript}
          isCompactSquare={isCompactSquare}
          initialSeekTimeSeconds={initialSeekTimeSeconds}
        />
      )}
    </InterviewDetailPanel>
  );
}

interface RecordingPlayerProps {
  recordingUrl: string | null;
  durationSeconds: number | null;
  showSkipControls: boolean;
  autoScrollTranscript: boolean;
  isCompactSquare?: boolean | undefined;
  initialSeekTimeSeconds?: number | undefined;
}

function NativeRecordingPlayer({
  recordingUrl,
  durationSeconds,
  showSkipControls,
  autoScrollTranscript,
  isCompactSquare = false,
  initialSeekTimeSeconds,
}: RecordingPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const lastSeekRequestIdRef = useRef<number | null>(null);
  const lastInitialSeekTimeRef = useRef<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [loadedDuration, setLoadedDuration] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [recordingInteraction, setRecordingInteraction] =
    useDetailPanelInteractionValue<InterviewRecordingInteractionState>(
      INTERVIEW_RECORDING_INTERACTION_KEY
    );
  const effectiveDuration = loadedDuration || durationSeconds || 0;
  const hasRecording = Boolean(recordingUrl) && !hasError;

  useEffect(() => {
    setRecordingInteraction({
      currentTimeSeconds: currentTime,
      durationSeconds: effectiveDuration,
      isPlaying,
      autoScrollTranscript,
    });
  }, [autoScrollTranscript, currentTime, effectiveDuration, isPlaying, setRecordingInteraction]);

  useEffect(() => {
    const seekRequest = recordingInteraction?.seekRequest;
    if (!seekRequest || lastSeekRequestIdRef.current === seekRequest.id || !hasRecording) {
      return;
    }

    lastSeekRequestIdRef.current = seekRequest.id;
    seekTo(seekRequest.timeSeconds);
    if (seekRequest.playAfterSeek && !isPlaying) {
      void playRecording();
    }
  }, [hasRecording, isPlaying, recordingInteraction?.seekRequest]);

  useEffect(() => {
    if (
      initialSeekTimeSeconds === undefined ||
      lastInitialSeekTimeRef.current === initialSeekTimeSeconds ||
      !hasRecording
    ) {
      return;
    }

    lastInitialSeekTimeRef.current = initialSeekTimeSeconds;
    seekTo(initialSeekTimeSeconds);
  }, [hasRecording, initialSeekTimeSeconds]);

  function syncCurrentTime(audio: HTMLAudioElement) {
    setCurrentTime(clampAudioTime(audio.currentTime, effectiveDuration));
  }

  function seekTo(seconds: number) {
    const nextTime = clampAudioTime(seconds, effectiveDuration);
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = nextTime;
      syncCurrentTime(audio);
      return;
    }
    setCurrentTime(nextTime);
  }

  async function playRecording() {
    const audio = audioRef.current;
    if (!audio || !hasRecording) {
      return;
    }

    try {
      await audio.play();
      setIsPlaying(true);
    } catch {
      setIsPlaying(false);
      setHasError(true);
    }
  }

  async function togglePlayback() {
    const audio = audioRef.current;
    if (!audio || !hasRecording) {
      return;
    }

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }

    await playRecording();
  }

  function handleSeek(value: string) {
    seekTo(Number(value));
  }

  return (
    <div
      className={[
        'flex flex-col justify-between',
        isCompactSquare ? 'h-full min-h-0 gap-3' : 'min-h-[7rem] gap-5',
      ].join(' ')}
    >
      {recordingUrl ? (
        <audio
          ref={audioRef}
          className="hidden"
          src={recordingUrl}
          preload="metadata"
          onLoadedMetadata={(event) => {
            const duration = event.currentTarget.duration;
            if (Number.isFinite(duration)) {
              setLoadedDuration(duration);
              setCurrentTime(clampAudioTime(event.currentTarget.currentTime, duration));
            }
          }}
          onTimeUpdate={(event) => syncCurrentTime(event.currentTarget)}
          onSeeked={(event) => syncCurrentTime(event.currentTarget)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => {
            setIsPlaying(false);
            const audio = audioRef.current;
            if (audio) {
              syncCurrentTime(audio);
            }
          }}
          onError={() => {
            setIsPlaying(false);
            setHasError(true);
          }}
        />
      ) : null}

      <div
        className={[
          'flex flex-1 items-center justify-center',
          isCompactSquare ? 'gap-2' : 'gap-3',
        ].join(' ')}
      >
        {showSkipControls ? (
          <button
            type="button"
            className={[
              'focus-ring inline-flex items-center justify-center gap-1 rounded-full border border-neutral-200 bg-white text-small font-semibold text-neutral-600 transition hover:border-neutral-300 hover:bg-backoffice-50 disabled:cursor-not-allowed disabled:border-neutral-200 disabled:bg-neutral-50 disabled:text-neutral-300',
              isCompactSquare ? 'h-8 min-w-11 px-2' : 'h-10 min-w-14 px-3',
            ].join(' ')}
            aria-label="Rewind recording 10 seconds"
            disabled={!hasRecording}
            onClick={() => seekTo(currentTime - 10)}
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
            <span>10s</span>
          </button>
        ) : null}
        <RecordingPlayButton
          isPlaying={isPlaying}
          disabled={!hasRecording}
          onClick={() => {
            void togglePlayback();
          }}
        />
        {showSkipControls ? (
          <button
            type="button"
            className={[
              'focus-ring inline-flex items-center justify-center gap-1 rounded-full border border-neutral-200 bg-white text-small font-semibold text-neutral-600 transition hover:border-neutral-300 hover:bg-backoffice-50 disabled:cursor-not-allowed disabled:border-neutral-200 disabled:bg-neutral-50 disabled:text-neutral-300',
              isCompactSquare ? 'h-8 min-w-11 px-2' : 'h-10 min-w-14 px-3',
            ].join(' ')}
            aria-label="Skip recording forward 10 seconds"
            disabled={!hasRecording}
            onClick={() => seekTo(currentTime + 10)}
          >
            <span>10s</span>
            <RotateCw className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      <div className="space-y-2">
        <RecordingTimeAndSeek
          currentTime={currentTime}
          duration={effectiveDuration}
          disabled={!hasRecording || !effectiveDuration}
          onSeek={handleSeek}
        />
        {!hasRecording ? (
          <p className="text-center text-small text-neutral-500">No recording available</p>
        ) : null}
      </div>
    </div>
  );
}

interface WaveformRecordingPlayerProps {
  recordingUrl: string;
  waveformSourceUrl?: string | null | undefined;
  waveformPeaks?: RecordingWaveformPeaks | null | undefined;
  durationSeconds: number | null;
  onWaveformError: (error: Error) => void;
  showSkipControls: boolean;
  autoScrollTranscript: boolean;
  initialSeekTimeSeconds?: number | undefined;
}

function WaveformRecordingPlayer({
  recordingUrl,
  waveformSourceUrl,
  waveformPeaks,
  durationSeconds,
  onWaveformError,
  showSkipControls,
  autoScrollTranscript,
  initialSeekTimeSeconds,
}: WaveformRecordingPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const lastSeekRequestIdRef = useRef<number | null>(null);
  const lastInitialSeekTimeRef = useRef<number | null>(null);
  const [nativeIsPlaying, setNativeIsPlaying] = useState(false);
  const [nativeCurrentTime, setNativeCurrentTime] = useState(0);
  const [loadedDuration, setLoadedDuration] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [recordingInteraction, setRecordingInteraction] =
    useDetailPanelInteractionValue<InterviewRecordingInteractionState>(
      INTERVIEW_RECORDING_INTERACTION_KEY
    );
  const splitChannels = useMemo(
    () => [
      {
        height: 56,
        waveColor: '#5e35bc',
        progressColor: '#b8a5ff',
      },
      {
        height: 56,
        waveColor: '#b65f5f',
        progressColor: '#f0a0a0',
      },
      {
        height: 56,
        waveColor: '#8257f5',
        progressColor: '#d4c9ff',
      },
      {
        height: 56,
        waveColor: '#9879ff',
        progressColor: '#e4ddff',
      },
      {
        height: 56,
        waveColor: '#5e35bc',
        progressColor: '#b8a5ff',
      },
      {
        height: 56,
        waveColor: '#7141e0',
        progressColor: '#c8b8ff',
      },
      {
        height: 56,
        waveColor: '#8257f5',
        progressColor: '#d4c9ff',
      },
      {
        height: 56,
        waveColor: '#9879ff',
        progressColor: '#e4ddff',
      },
    ],
    []
  );
  const waveformOptions = useMemo(() => {
    const baseOptions = {
      container: waveformRef,
      height: 56,
      normalize: true,
      barWidth: 2,
      barGap: 1,
      cursorWidth: 2,
      cursorColor: '#4e2d99',
      dragToSeek: true,
      hideScrollbar: true,
      waveColor: '#5e35bc',
      progressColor: '#b8a5ff',
      splitChannels,
    };

    return waveformPeaks
      ? {
          ...baseOptions,
          peaks: waveformPeaks.peaks,
          duration: waveformPeaks.durationSeconds,
        }
      : {
          ...baseOptions,
          url: waveformSourceUrl ?? recordingUrl,
        };
  }, [recordingUrl, splitChannels, waveformPeaks, waveformSourceUrl]);
  const { wavesurfer, isReady } = useWavesurfer(waveformOptions);
  const showWaveformLoading = !isReady && !waveformPeaks;
  const effectiveDuration =
    loadedDuration ||
    waveformPeaks?.durationSeconds ||
    wavesurfer?.getDuration() ||
    durationSeconds ||
    0;
  const hasRecording = Boolean(recordingUrl) && !hasError;

  useEffect(() => {
    setRecordingInteraction({
      currentTimeSeconds: nativeCurrentTime,
      durationSeconds: effectiveDuration,
      isPlaying: nativeIsPlaying,
      autoScrollTranscript,
    });
  }, [
    autoScrollTranscript,
    effectiveDuration,
    nativeCurrentTime,
    nativeIsPlaying,
    setRecordingInteraction,
  ]);

  useEffect(() => {
    return wavesurfer?.on('error', onWaveformError);
  }, [wavesurfer, onWaveformError]);

  useEffect(() => {
    if (!wavesurfer || !isReady) {
      return;
    }

    wavesurfer.setTime(clampAudioTime(nativeCurrentTime, effectiveDuration));
  }, [effectiveDuration, isReady, nativeCurrentTime, wavesurfer]);

  useEffect(() => {
    if (!nativeIsPlaying || !wavesurfer || !isReady) {
      return;
    }

    let animationFrameId: number | undefined;
    const syncWaveformCursor = () => {
      const audio = audioRef.current;
      if (audio) {
        wavesurfer.setTime(clampAudioTime(audio.currentTime, effectiveDuration));
      }
      animationFrameId = window.requestAnimationFrame(syncWaveformCursor);
    };

    syncWaveformCursor();
    return () => {
      if (animationFrameId !== undefined) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [effectiveDuration, isReady, nativeIsPlaying, wavesurfer]);

  useEffect(() => {
    return wavesurfer?.on('interaction', (timeSeconds: number) => {
      seekTo(timeSeconds);
    });
  }, [wavesurfer, effectiveDuration]);

  useEffect(() => {
    const seekRequest = recordingInteraction?.seekRequest;
    if (!seekRequest || lastSeekRequestIdRef.current === seekRequest.id || !hasRecording) {
      return;
    }

    lastSeekRequestIdRef.current = seekRequest.id;
    seekTo(seekRequest.timeSeconds);
    if (seekRequest.playAfterSeek && !nativeIsPlaying) {
      void playRecording();
    }
  }, [hasRecording, nativeIsPlaying, recordingInteraction?.seekRequest]);

  useEffect(() => {
    if (
      initialSeekTimeSeconds === undefined ||
      lastInitialSeekTimeRef.current === initialSeekTimeSeconds ||
      !hasRecording
    ) {
      return;
    }

    lastInitialSeekTimeRef.current = initialSeekTimeSeconds;
    seekTo(initialSeekTimeSeconds);
  }, [hasRecording, initialSeekTimeSeconds]);

  function syncCurrentTime(audio: HTMLAudioElement) {
    setNativeCurrentTime(clampAudioTime(audio.currentTime, effectiveDuration));
  }

  function seekTo(seconds: number) {
    const nextTime = clampAudioTime(seconds, effectiveDuration);
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = nextTime;
      syncCurrentTime(audio);
    } else {
      setNativeCurrentTime(nextTime);
    }

    if (wavesurfer && isReady) {
      wavesurfer.setTime(nextTime);
    }
  }

  async function playRecording() {
    const audio = audioRef.current;
    if (!audio || !hasRecording) {
      return;
    }

    try {
      await audio.play();
      setNativeIsPlaying(true);
    } catch {
      setNativeIsPlaying(false);
      setHasError(true);
    }
  }

  async function togglePlayback() {
    const audio = audioRef.current;
    if (!audio || !hasRecording) {
      return;
    }

    if (nativeIsPlaying) {
      audio.pause();
      setNativeIsPlaying(false);
      return;
    }

    await playRecording();
  }

  return (
    <div className="flex h-full min-h-0 flex-col justify-between gap-3">
      <audio
        ref={audioRef}
        className="hidden"
        src={recordingUrl}
        preload="auto"
        onLoadedMetadata={(event) => {
          const duration = event.currentTarget.duration;
          if (Number.isFinite(duration)) {
            setLoadedDuration(duration);
            setNativeCurrentTime(clampAudioTime(event.currentTarget.currentTime, duration));
          }
        }}
        onTimeUpdate={(event) => syncCurrentTime(event.currentTarget)}
        onSeeked={(event) => syncCurrentTime(event.currentTarget)}
        onPlay={() => setNativeIsPlaying(true)}
        onPause={() => setNativeIsPlaying(false)}
        onEnded={() => {
          setNativeIsPlaying(false);
          const audio = audioRef.current;
          if (audio) {
            syncCurrentTime(audio);
          }
        }}
        onError={() => {
          setNativeIsPlaying(false);
          setHasError(true);
        }}
      />
      <div
        className="relative overflow-hidden rounded-md border border-neutral-200 bg-white px-3 pb-7 pt-3"
        data-testid="recording-waveform-panel"
      >
        {waveformPeaks?.channelRoles?.length ? (
          <div
            className="mb-1 flex gap-4 text-xs text-neutral-600"
            data-testid="recording-channel-labels"
          >
            {waveformPeaks.channelRoles.map((role, index) => (
              <span key={`${role}-${index}`} className="inline-flex items-center gap-1">
                <span
                  className={
                    index === 0
                      ? 'size-2 rounded-full bg-lavender-700'
                      : 'size-2 rounded-full bg-red-400'
                  }
                  aria-hidden="true"
                />
                {index + 1}. {role === 'interviewer' ? 'Nava' : 'Participant'}
              </span>
            ))}
          </div>
        ) : null}
        <div
          ref={waveformRef}
          className="no-tile-drag min-h-[4rem]"
          aria-label="Recording waveform"
          data-testid="recording-waveform"
        />
        {showWaveformLoading ? (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-small font-medium text-neutral-500">
            Loading waveform
          </div>
        ) : null}
        <RecordingTimeRow currentTime={nativeCurrentTime} duration={effectiveDuration} />
      </div>

      <div className="flex items-center justify-center gap-3">
        {showSkipControls ? (
          <button
            type="button"
            className="focus-ring inline-flex h-10 min-w-14 items-center justify-center gap-1 rounded-full border border-neutral-200 bg-white px-3 text-small font-semibold text-neutral-600 transition hover:border-neutral-300 hover:bg-backoffice-50 disabled:cursor-not-allowed disabled:border-neutral-200 disabled:bg-neutral-50 disabled:text-neutral-300"
            aria-label="Rewind recording 10 seconds"
            disabled={!hasRecording}
            onClick={() => seekTo(nativeCurrentTime - 10)}
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
            <span>10s</span>
          </button>
        ) : null}
        <RecordingPlayButton
          isPlaying={nativeIsPlaying}
          disabled={!hasRecording}
          onClick={() => {
            void togglePlayback();
          }}
        />
        {showSkipControls ? (
          <button
            type="button"
            className="focus-ring inline-flex h-10 min-w-14 items-center justify-center gap-1 rounded-full border border-neutral-200 bg-white px-3 text-small font-semibold text-neutral-600 transition hover:border-neutral-300 hover:bg-backoffice-50 disabled:cursor-not-allowed disabled:border-neutral-200 disabled:bg-neutral-50 disabled:text-neutral-300"
            aria-label="Skip recording forward 10 seconds"
            disabled={!hasRecording}
            onClick={() => seekTo(nativeCurrentTime + 10)}
          >
            <span>10s</span>
            <RotateCw className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

interface RecordingTimeRowProps {
  currentTime: number;
  duration: number;
}

function RecordingTimeRow({ currentTime, duration }: RecordingTimeRowProps) {
  return (
    <div className="pointer-events-none absolute bottom-2 left-3 right-3 flex justify-between text-small font-medium tabular-nums text-neutral-600">
      <span>{formatAudioTime(currentTime)}</span>
      <span>{duration ? formatAudioTime(duration) : formatDuration(null)}</span>
    </div>
  );
}

interface RecordingPlayButtonProps {
  isPlaying: boolean;
  disabled: boolean;
  onClick: () => void;
}

function RecordingPlayButton({ isPlaying, disabled, onClick }: RecordingPlayButtonProps) {
  return (
    <button
      type="button"
      className="focus-ring inline-flex h-10 w-10 items-center justify-center rounded-full bg-backoffice-500 text-white transition hover:bg-backoffice-600 disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:text-neutral-400"
      aria-label={isPlaying ? 'Pause recording' : 'Play recording'}
      disabled={disabled}
      onClick={onClick}
    >
      {isPlaying ? (
        <Pause className="h-4 w-4" aria-hidden="true" />
      ) : (
        <Play className="ml-0.5 h-4 w-4" aria-hidden="true" />
      )}
    </button>
  );
}

interface RecordingTimeAndSeekProps {
  currentTime: number;
  duration: number;
  disabled: boolean;
  onSeek: (value: string) => void;
  className?: string | undefined;
}

function RecordingTimeAndSeek({
  currentTime,
  duration,
  disabled,
  onSeek,
  className,
}: RecordingTimeAndSeekProps) {
  return (
    <div className={['space-y-2', className].filter(Boolean).join(' ')}>
      <div className="flex justify-between text-small font-medium tabular-nums text-neutral-600">
        <span>{formatAudioTime(currentTime)}</span>
        <span>{duration ? formatAudioTime(duration) : formatDuration(null)}</span>
      </div>
      <input
        type="range"
        min="0"
        max={duration || 0}
        step="1"
        value={Math.min(currentTime, duration || currentTime)}
        disabled={disabled}
        aria-label="Seek recording"
        className="w-full accent-lavender-600 disabled:cursor-not-allowed disabled:opacity-50"
        onChange={(event) => onSeek(event.target.value)}
      />
    </div>
  );
}
